import React, { useState, useEffect } from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import {
  Archive,
  Dashboard,
  List as ListIcon,
  Unarchive,
  Add,
  Home,
  Assessment,
  Schedule
} from '@mui/icons-material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ArchiveList from '../components/mrd/Archive/ArchiveList'
import ArchiveDashboard from '../components/mrd/Archive/ArchiveDashboard'
import ArchiveForm from '../components/mrd/Archive/ArchiveForm'
import archiveService from '../services/archiveService'

export const ArchivePage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fileIdFromUrl = searchParams.get('fileId')
  
  const [tabValue, setTabValue] = useState(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [openArchiveDialog, setOpenArchiveDialog] = useState(!!fileIdFromUrl)
  const [selectedFileId, setSelectedFileId] = useState(fileIdFromUrl)

  useEffect(() => {
    if (tabValue === 0) {
      loadDashboardData()
    }
  }, [tabValue])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const response = await archiveService.getArchiveDashboard()
      if (response.data.success) {
        setDashboardData(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleArchiveSuccess = (archiveData) => {
    setOpenArchiveDialog(false)
    setSelectedFileId(null)
    setRefreshKey(prev => prev + 1)
    loadDashboardData()
  }

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return <ArchiveDashboard data={dashboardData} loading={loading} />
      case 1:
        return (
          <ArchiveList 
            key={refreshKey}
            onEdit={(archive) => {
              console.log('Edit archive:', archive)
            }}
            onView={(archive) => {
              console.log('View archive:', archive)
            }}
          />
        )
      // case 2:
      //   return (
      //     <ArchiveList 
      //       key={`retrieval-${refreshKey}`}
      //       statusFilterProps="RETRIEVED"
      //       onEdit={(archive) => {
      //         console.log('Edit archive:', archive)
      //       }}
      //       onView={(archive) => {
      //         console.log('View archive:', archive)
      //       }}
      //     />
      //   )
      default:
        return null
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          color="inherit"
          onClick={() => navigate('/dashboard')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Dashboard
        </Link>
        <Typography
          sx={{ display: 'flex', alignItems: 'center' }}
          color="text.primary"
        >
          <Archive sx={{ mr: 0.5 }} fontSize="small" />
          Archive Management
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Archive Management System
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive archive management with retention policies and destruction scheduling
          </Typography>
        </Box>
        
      </Box>

      {/* Quick Stats */}
      {dashboardData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="primary" fontWeight={700}>
                  {dashboardData.summary?.totalArchives || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Total Archives
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="success.main" fontWeight={700}>
                  {dashboardData.summary?.recentArchives || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Recent Archives (30 days)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="warning.main" fontWeight={700}>
                  {dashboardData.summary?.pendingDestruction || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Pending Destruction
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="info.main" fontWeight={700}>
                  {dashboardData.summary?.accessedThisMonth || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Accessed This Month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, val) => setTabValue(val)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 72,
              fontSize: '1rem',
              fontWeight: 600
            }
          }}
        >
          <Tab 
            label="Dashboard" 
            icon={<Dashboard />}
            iconPosition="start"
          />
          <Tab 
            label="Archived Files" 
            icon={<Archive />}
            iconPosition="start"
          />
          {/* <Tab 
            label="Retrieved Files" 
            icon={<Unarchive />}
            iconPosition="start"
          /> */}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ borderRadius: 2 }}>
        {renderTabContent()}
      </Paper>

      {/* Archive Dialog */}
      <Dialog 
        open={openArchiveDialog} 
        onClose={() => setOpenArchiveDialog(false)}
        maxWidth="lg"
        fullWidth
      > 
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Archive MRD File
          </Typography>
        </DialogTitle>
        <DialogContent>
          <ArchiveForm
            fileId={selectedFileId}
            onSuccess={handleArchiveSuccess}
            onCancel={() => setOpenArchiveDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Container>
  )
}

